import React from 'react';
import { ScrollView, StyleSheet, Text, View } from 'react-native';

import { GlassButton } from '../../components/ui/GlassButton';
import { GlassCard } from '../../components/ui/GlassCard';
import { APP_NAME } from '../../config/branding';
import { navigateToAuth } from '../../core/navigation/navigationRef';
import { colors, radii, spacing, typography } from '../../core/theme/tokens';
import { useAuth } from '../../contexts/AuthContext';
import { useImportOnboarding } from '../../contexts/ImportOnboardingContext';
import { useOnboarding } from '../../contexts/OnboardingContext';
import {
  COUNTRY_OPTIONS,
  DEFAULT_EXPECTED_RETURN_PCT,
  MAX_EXPECTED_RETURN_PCT,
  MIN_EXPECTED_RETURN_PCT,
  SPEND_CATEGORY_OPTIONS,
  currencyLabel,
  formatMoney,
  getIncomeMarks,
  getLiabilityCatalog,
} from '../../data/countryInvestmentCatalog';
import type { OnboardingAsset, OnboardingLiability } from '../../types/onboarding';
import { ONBOARDING_STEP } from '../../types/onboarding';
import { detectCountryFromLocale } from '../../utils/detectLocaleCountry';
import { estimatePaymentSplit } from '../../utils/loanAmortization';
import { ValueStepper } from './components/ValueStepper';

type OnboardingModalContentProps = {
  onRequestClose: () => void;
  /** Fixed viewport height so header + scroll + footer fit without clipping Continue. */
  maxHeight: number;
};

export function OnboardingModalContent({ onRequestClose, maxHeight }: OnboardingModalContentProps) {
  const {
    draft,
    browseSample,
    updateDraft,
    setCountry,
    goToNextStep,
    goToPrevStep,
  } = useOnboarding();
  const { isAuthenticated } = useAuth();
  const { openImportMore } = useImportOnboarding();

  const marks = getIncomeMarks(draft.countryCode);
  const step = draft.step;
  const currencyPrefix = marks.currencySymbol;
  const detectedCountry = detectCountryFromLocale();
  const countryWasAutoDetected = draft.countryCode === detectedCountry;
  const showStepFooter = step > ONBOARDING_STEP.INTRO && step < ONBOARDING_STEP.UPLOAD;
  const showUploadFooter = step === ONBOARDING_STEP.UPLOAD;

  const handleBrowse = async () => {
    await browseSample();
  };

  const handleSkipLiabilities = async () => {
    await goToNextStep();
  };

  const handleUploadSignIn = async () => {
    await updateDraft({ dismissedModal: true, completedIntro: true });
    onRequestClose();
    navigateToAuth();
  };

  const handleUploadInvestmentScreenshot = async () => {
    if (!isAuthenticated) {
      await handleUploadSignIn();
      return;
    }
    await updateDraft({ dismissedModal: true, completedIntro: true });
    onRequestClose();
    openImportMore();
  };

  const clampIncome = (value: number) =>
    Math.max(marks.min, Math.min(marks.max, value));

  const updateAsset = async (index: number, patch: Partial<OnboardingAsset>) => {
    const assets = draft.assets.map((asset, i) => (i === index ? { ...asset, ...patch } : asset));
    await updateDraft({ assets });
  };

  const updateLiability = async (index: number, patch: Partial<OnboardingLiability>) => {
    const liabilities = draft.liabilities.map((item, i) => {
      if (i !== index) return item;
      const next = { ...item, ...patch };
      const catalog = getLiabilityCatalog().find((c) => c.type === next.type);
      if (catalog?.detailed && next.balance != null && next.interestRatePct != null && next.monthlyPayment != null) {
        const split = estimatePaymentSplit(next.balance, next.interestRatePct, next.monthlyPayment);
        return { ...next, ...split };
      }
      return next;
    });
    await updateDraft({ liabilities });
  };

  const setSpendAmount = async (categoryId: string, amount: number) => {
    const existing = draft.expectedSpendByCategory.filter((c) => c.categoryId !== categoryId);
    const next =
      amount <= 0
        ? existing
        : [...existing, { categoryId, amount }];
    await updateDraft({ expectedSpendByCategory: next });
  };

  const spendAmount = (categoryId: string) =>
    draft.expectedSpendByCategory.find((c) => c.categoryId === categoryId)?.amount ?? 0;

  return (
    <View style={[styles.root, { maxHeight }]}>
      <View style={styles.header}>
        <Text style={styles.kicker}>{APP_NAME}</Text>
        {step > ONBOARDING_STEP.INTRO ? (
          <Text style={styles.progress}>
            Step {step} of {ONBOARDING_STEP.UPLOAD}
          </Text>
        ) : null}
      </View>

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="interactive"
        nestedScrollEnabled
        bounces
      >
        {step === ONBOARDING_STEP.INTRO ? (
          <View style={styles.intro}>
            <Text style={styles.title}>Preview with sample numbers</Text>
            <Text style={styles.subtitle}>
              Explore the dashboard, then sign in to upload bank, card, or brokerage screenshots
              and replace this with your real picture.
            </Text>
            <GlassButton
              variant="primary"
              fullWidth
              onPress={handleUploadSignIn}
              style={styles.cta}
            >
              Sign in to upload
            </GlassButton>
            <GlassButton variant="secondary" fullWidth onPress={handleBrowse}>
              Browse sample data
            </GlassButton>
          </View>
        ) : null}

        {step === ONBOARDING_STEP.COUNTRY_INCOME ? (
          <View style={styles.stepBody}>
            <Text style={styles.title}>Where do you live?</Text>
            <Text style={styles.subtitle}>
              Preselected from your device locale. Currency updates with country — sample
              playground numbers stay as shown (no conversion).
            </Text>
            {countryWasAutoDetected ? (
              <Text style={styles.detectHint}>
                Detected: {COUNTRY_OPTIONS.find((c) => c.code === draft.countryCode)?.label} ·{' '}
                {currencyLabel(draft.countryCode)}
              </Text>
            ) : (
              <Text style={styles.detectHint}>
                Currency: {currencyLabel(draft.countryCode)}
              </Text>
            )}
            <View style={styles.chipRow}>
              {COUNTRY_OPTIONS.map((option) => (
                <GlassButton
                  key={option.code}
                  variant="secondary"
                  size="sm"
                  selected={draft.countryCode === option.code}
                  onPress={() => setCountry(option.code)}
                >
                  {option.label}
                </GlassButton>
              ))}
            </View>
            <GlassCard style={styles.cardGap}>
              <ValueStepper
                label="Monthly income after tax"
                value={draft.monthlyIncomeAfterTax}
                prefix={currencyPrefix}
                hint={`Typical mid ${formatMoney(marks.mid, draft.countryCode)} · high ${formatMoney(marks.high, draft.countryCode)}`}
                min={marks.min}
                max={marks.max}
                onChange={(monthlyIncomeAfterTax) =>
                  updateDraft({ monthlyIncomeAfterTax: clampIncome(monthlyIncomeAfterTax) })
                }
                onDecrease={() =>
                  updateDraft({
                    monthlyIncomeAfterTax: clampIncome(draft.monthlyIncomeAfterTax - marks.step),
                  })
                }
                onIncrease={() =>
                  updateDraft({
                    monthlyIncomeAfterTax: clampIncome(draft.monthlyIncomeAfterTax + marks.step),
                  })
                }
              />
            </GlassCard>
          </View>
        ) : null}

        {step === ONBOARDING_STEP.ASSETS ? (
          <View style={styles.stepBody}>
            <Text style={styles.title}>Assets & investments</Text>
            <Text style={styles.subtitle}>
              Enter balances manually, or upload a screenshot from Wealthsimple, your brokerage,
              or another wealth app. Long-run equity returns often land around 6–8%; default is{' '}
              {DEFAULT_EXPECTED_RETURN_PCT}%.
            </Text>

            <GlassCard style={styles.uploadCard}>
              <Text style={styles.cardTitle}>Upload from an app</Text>
              <Text style={styles.uploadHint}>
                Same import as statements — bank/cards for spend, or Wealthsimple / Robinhood /
                Fidelity screenshots for investment balances.
              </Text>
              <GlassButton
                variant="primary"
                fullWidth
                onPress={handleUploadInvestmentScreenshot}
              >
                {isAuthenticated
                  ? 'Import statements & screenshots'
                  : 'Sign in to import screenshots'}
              </GlassButton>
              <Text style={styles.uploadOr}>or enter values below</Text>
            </GlassCard>

            {draft.assets.map((asset, index) => (
              <GlassCard key={`${asset.type}-${index}`} style={styles.cardGap}>
                <Text style={styles.cardTitle}>{asset.label}</Text>
                <ValueStepper
                  label="Value"
                  value={asset.value}
                  prefix={currencyPrefix}
                  min={0}
                  onChange={(value) => updateAsset(index, { value: Math.max(0, value) })}
                  onDecrease={() =>
                    updateAsset(index, { value: Math.max(0, asset.value - marks.step * 4) })
                  }
                  onIncrease={() => updateAsset(index, { value: asset.value + marks.step * 4 })}
                />
                <View style={styles.chipRow}>
                  <GlassButton
                    variant="secondary"
                    size="sm"
                    selected={asset.isInvestment}
                    onPress={() =>
                      updateAsset(index, {
                        isInvestment: true,
                        expectedReturnPct: asset.expectedReturnPct ?? DEFAULT_EXPECTED_RETURN_PCT,
                      })
                    }
                  >
                    Investment
                  </GlassButton>
                  <GlassButton
                    variant="secondary"
                    size="sm"
                    selected={!asset.isInvestment}
                    onPress={() =>
                      updateAsset(index, { isInvestment: false, expectedReturnPct: undefined })
                    }
                  >
                    Not an investment
                  </GlassButton>
                </View>
                {asset.isInvestment ? (
                  <ValueStepper
                    label="Expected return"
                    value={asset.expectedReturnPct ?? DEFAULT_EXPECTED_RETURN_PCT}
                    suffix="%"
                    hint="Typical long-run range 4–12%"
                    decimals
                    min={MIN_EXPECTED_RETURN_PCT}
                    max={MAX_EXPECTED_RETURN_PCT}
                    onChange={(expectedReturnPct) => updateAsset(index, { expectedReturnPct })}
                    onDecrease={() =>
                      updateAsset(index, {
                        expectedReturnPct: Math.max(
                          MIN_EXPECTED_RETURN_PCT,
                          (asset.expectedReturnPct ?? DEFAULT_EXPECTED_RETURN_PCT) - 1,
                        ),
                      })
                    }
                    onIncrease={() =>
                      updateAsset(index, {
                        expectedReturnPct: Math.min(
                          MAX_EXPECTED_RETURN_PCT,
                          (asset.expectedReturnPct ?? DEFAULT_EXPECTED_RETURN_PCT) + 1,
                        ),
                      })
                    }
                  />
                ) : null}
              </GlassCard>
            ))}
          </View>
        ) : null}

        {step === ONBOARDING_STEP.LIABILITIES ? (
          <View style={styles.stepBody}>
            <Text style={styles.title}>Liabilities</Text>
            <Text style={styles.subtitle}>
              For a home loan, add rate and payment — we’ll estimate how much of this month’s
              payment is interest vs principal.
            </Text>
            {draft.liabilities.map((liability, index) => {
              const catalog = getLiabilityCatalog().find((c) => c.type === liability.type);
              const detailed = catalog?.detailed ?? false;
              return (
                <GlassCard key={`${liability.type}-${index}`} style={styles.cardGap}>
                  <Text style={styles.cardTitle}>{liability.label}</Text>
                  <ValueStepper
                    label="Balance"
                    value={liability.balance}
                    prefix={currencyPrefix}
                    min={0}
                    onChange={(balance) => updateLiability(index, { balance: Math.max(0, balance) })}
                    onDecrease={() =>
                      updateLiability(index, {
                        balance: Math.max(0, liability.balance - marks.step * 10),
                      })
                    }
                    onIncrease={() =>
                      updateLiability(index, { balance: liability.balance + marks.step * 10 })
                    }
                  />
                  {detailed || liability.balance > 0 ? (
                    <>
                      <ValueStepper
                        label="Interest rate"
                        value={liability.interestRatePct ?? 0}
                        suffix="%"
                        decimals
                        min={0}
                        max={40}
                        onChange={(interestRatePct) =>
                          updateLiability(index, { interestRatePct })
                        }
                        onDecrease={() =>
                          updateLiability(index, {
                            interestRatePct: Math.max(
                              0,
                              Math.round(((liability.interestRatePct ?? 0) - 0.25) * 100) / 100,
                            ),
                          })
                        }
                        onIncrease={() =>
                          updateLiability(index, {
                            interestRatePct:
                              Math.round(((liability.interestRatePct ?? 0) + 0.25) * 100) / 100,
                          })
                        }
                      />
                      <ValueStepper
                        label="Monthly payment"
                        value={liability.monthlyPayment ?? 0}
                        prefix={currencyPrefix}
                        min={0}
                        onChange={(monthlyPayment) =>
                          updateLiability(index, { monthlyPayment: Math.max(0, monthlyPayment) })
                        }
                        onDecrease={() =>
                          updateLiability(index, {
                            monthlyPayment: Math.max(0, (liability.monthlyPayment ?? 0) - 50),
                          })
                        }
                        onIncrease={() =>
                          updateLiability(index, {
                            monthlyPayment: (liability.monthlyPayment ?? 0) + 50,
                          })
                        }
                      />
                    </>
                  ) : null}
                  {detailed &&
                  (liability.monthlyInterest != null || liability.monthlyPrincipal != null) &&
                  (liability.monthlyPayment ?? 0) > 0 ? (
                    <View style={styles.splitBox}>
                      <Text style={styles.splitText}>
                        Of your {formatMoney(liability.monthlyPayment ?? 0, draft.countryCode)}{' '}
                        payment,{' '}
                        {formatMoney(liability.monthlyInterest ?? 0, draft.countryCode)} is
                        interest,{' '}
                        {formatMoney(liability.monthlyPrincipal ?? 0, draft.countryCode)} pays
                        down the loan.
                      </Text>
                    </View>
                  ) : null}
                </GlassCard>
              );
            })}
            <GlassButton variant="ghost" fullWidth onPress={handleSkipLiabilities}>
              Skip — add later
            </GlassButton>
          </View>
        ) : null}

        {step === ONBOARDING_STEP.SPEND ? (
          <View style={styles.stepBody}>
            <Text style={styles.title}>Expected monthly spend</Text>
            <Text style={styles.subtitle}>Set targets by category. Tap chips to adjust.</Text>
            {SPEND_CATEGORY_OPTIONS.map((category) => {
              const amount = spendAmount(category.id);
              return (
                <GlassCard key={category.id} style={styles.cardGap}>
                  <ValueStepper
                    label={category.name}
                    value={amount}
                    prefix={currencyPrefix}
                    min={0}
                    onChange={(next) => setSpendAmount(category.id, Math.max(0, next))}
                    onDecrease={() => setSpendAmount(category.id, Math.max(0, amount - 50))}
                    onIncrease={() => setSpendAmount(category.id, amount + 50)}
                  />
                </GlassCard>
              );
            })}
          </View>
        ) : null}

        {step === ONBOARDING_STEP.UPLOAD ? (
          <View style={styles.intro}>
            <Text style={styles.title}>Upload statements</Text>
            <Text style={styles.subtitle}>
              Sign in to upload screenshots from your bank, credit cards, Wealthsimple, or other
              investment apps.
            </Text>
            <GlassButton variant="primary" fullWidth onPress={handleUploadSignIn} style={styles.cta}>
              Sign in to upload
            </GlassButton>
            <GlassButton
              variant="secondary"
              fullWidth
              onPress={async () => {
                await updateDraft({ dismissedModal: true });
                onRequestClose();
              }}
            >
              Keep exploring sample data
            </GlassButton>
          </View>
        ) : null}
      </ScrollView>

      {showStepFooter ? (
        <View style={styles.footer}>
          <GlassButton variant="ghost" onPress={() => void goToPrevStep()}>
            Back
          </GlassButton>
          <GlassButton variant="primary" onPress={() => void goToNextStep()}>
            Continue
          </GlassButton>
        </View>
      ) : null}

      {showUploadFooter ? (
        <View style={styles.footer}>
          <GlassButton variant="ghost" onPress={() => void goToPrevStep()}>
            Back
          </GlassButton>
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.md,
    flexShrink: 0,
  },
  kicker: {
    ...typography.label,
    color: colors.accent,
    textTransform: 'uppercase',
  },
  progress: {
    ...typography.caption,
    color: colors.textMuted,
  },
  scroll: {
    flex: 1,
    minHeight: 0,
  },
  scrollContent: {
    paddingBottom: spacing.md,
    gap: spacing.sm,
    flexGrow: 1,
  },
  intro: {
    gap: spacing.md,
  },
  stepBody: {
    gap: spacing.sm,
  },
  title: {
    ...typography.title,
    color: colors.text,
    fontSize: 24,
    lineHeight: 30,
  },
  subtitle: {
    ...typography.body,
    color: colors.textMuted,
    marginBottom: spacing.sm,
  },
  detectHint: {
    ...typography.caption,
    color: colors.accent,
    marginBottom: spacing.sm,
  },
  cta: {
    marginTop: spacing.sm,
  },
  chipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  cardGap: {
    marginBottom: spacing.sm,
  },
  uploadCard: {
    marginBottom: spacing.md,
    gap: spacing.sm,
    borderColor: 'rgba(61, 214, 164, 0.28)',
  },
  uploadHint: {
    ...typography.caption,
    color: colors.textMuted,
    marginBottom: spacing.xs,
  },
  uploadOr: {
    ...typography.caption,
    color: colors.textMuted,
    textAlign: 'center',
    marginTop: spacing.xs,
  },
  cardTitle: {
    ...typography.headline,
    color: colors.text,
    fontSize: 16,
    marginBottom: spacing.sm,
  },
  splitBox: {
    marginTop: spacing.xs,
    padding: spacing.sm,
    borderRadius: radii.md,
    backgroundColor: 'rgba(245, 158, 11, 0.12)',
    borderWidth: 1,
    borderColor: 'rgba(245, 158, 11, 0.35)',
  },
  splitText: {
    ...typography.caption,
    color: colors.text,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: spacing.md,
    paddingTop: spacing.md,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.border,
    flexShrink: 0,
    zIndex: 2,
  },
});
